import { useState } from 'react';
import { Compass, Radio, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNowPlaying } from '../contexts/NowPlayingContext';
import { thumbUrl } from '../lib/imageUrl';

const NowPlayingDiscovery = ({ variant = 'floating' }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { media, discoveryPrompts } = useNowPlaying();
  const hasNowPlaying = Boolean(media.id || media.isRadio);
  if (!hasNowPlaying && variant !== 'action-bar') return null;

  const openDiscovery = () => {
    if (hasNowPlaying) setOpen(true);
    else navigate('/explore');
  };

  const selectPrompt = (prompt) => {
    setOpen(false);
    window.dispatchEvent(new CustomEvent('rastyle:discovery-action', {
      detail: { ...prompt, media },
    }));
  };

  return (
    <>
      <button
        type="button"
        onClick={openDiscovery}
        className={variant === 'action-bar'
          ? 'flex min-w-0 flex-col items-center justify-center gap-1 px-1 text-[10px] font-bold uppercase tracking-wide text-zinc-300 transition hover:bg-white/5 hover:text-white'
          : 'fixed bottom-24 right-6 z-40 hidden items-center gap-2 rounded-full border border-white/10 bg-black/90 px-4 py-3 text-xs font-bold uppercase tracking-wider text-white shadow-xl backdrop-blur md:flex'}
        data-testid={variant === 'action-bar' ? 'mobile-action-discover' : 'now-playing-discovery-button'}
        aria-label={hasNowPlaying ? 'Open now playing discovery' : 'Explore Rastyle'}
      >
        <Compass className={variant === 'action-bar' ? 'h-5 w-5 text-rastyle-orange' : 'h-4 w-4 text-rastyle-orange'} />
        <span>Discover</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[80] flex items-end bg-black/70" role="dialog" aria-modal="true" aria-label="Now playing discovery">
          <button type="button" className="absolute inset-0" onClick={() => setOpen(false)} aria-label="Close discovery" />
          <section className="relative z-10 max-h-[82vh] w-full overflow-y-auto rounded-t-3xl border-t border-white/10 bg-zinc-950 p-5 pb-10 sm:mx-auto sm:max-w-2xl sm:p-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div className="flex min-w-0 gap-4">
                <div className="h-20 w-20 flex-none overflow-hidden rounded-xl bg-zinc-900">
                  {media.cover ? <img src={thumbUrl(media.cover)} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center"><Radio /></div>}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-rastyle-orange">{media.isLive ? 'Live now' : 'Now playing'}</p>
                  <h2 className="mt-1 truncate text-2xl font-black text-white">{media.title}</h2>
                  <p className="truncate text-zinc-400">{media.artist}</p>
                  <p className="mt-2 text-xs uppercase tracking-wider text-zinc-500">{media.genre}</p>
                </div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-white/10 p-2 text-zinc-300" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {discoveryPrompts.map((prompt) => (
                <button
                  key={prompt.id}
                  type="button"
                  onClick={() => selectPrompt(prompt)}
                  className="rounded-2xl border border-white/10 bg-black p-4 text-left transition hover:border-rastyle-orange/60"
                >
                  <span className="text-xs font-bold uppercase tracking-wider text-rastyle-orange">{prompt.kind}</span>
                  <span className="mt-1 block font-bold text-white">{prompt.title}</span>
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default NowPlayingDiscovery;
